import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import { formatDayJSDate } from '@/components/utils/utils';
import * as P from '@/constants/paths';
import { toast } from '@/hooks/use-toast';
import useRegisterStore, {
  RegistrationTrack,
} from '@/hooks/useRegistrationStore';
import {
  useConfirmDetailsMutation,
  useUserConsentMutation,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import { useNavigate } from 'react-router-dom';
import { InfoSection } from './InfoSection';

export const RegistrationConfirmation = () => {
  const {
    kitId,
    email,
    firstName,
    lastName,
    phone,
    phoneCode,
    dob,
    street,
    country,
    city,
    apartment,
    zip,
    gender,
    shippable,
    state,
    setMinorConsent,
    setMajorConsent,
    setRestrictedRegistrationSteps,
  } = useRegisterStore();
  const navigate = useNavigate();

  const {
    isPending: isPendingConfirmation,
    error: errorConfirmation,
    mutate: mutateConfirmation,
  } = useConfirmDetailsMutation();

  const {
    isPending: isPendingConsent,
    error: errorConsent,
    mutate: mutateConsent,
  } = useUserConsentMutation();

  const dobReadableFormat = new Date(dob).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
  const formattedAddress = `${street}, ${apartment}
  ${city}, ${state} ${zip}
  ${country}`;

  const loading = isPendingConfirmation || isPendingConsent;

  const navigateToUserConsent = () => {
    navigate(P.REGISTRATION_TOS);
  };

  const callConsent = () => {
    mutateConsent(
      {
        countryCode: country,
        dateOfBirth: formatDayJSDate(dob),
        kitId,
      },
      {
        onSuccess: (data) => {
          const minConsent = data?.find((item: any) => item?.id === 133);
          if (minConsent) {
            setMinorConsent(minConsent);
            setMajorConsent(data[1]);
            Promise.all([
              setRestrictedRegistrationSteps([]),
              navigate(P.REGISTRATION_MINOR_CONSENT),
            ]);
          } else {
            setMinorConsent(undefined);
            setMajorConsent(data[0]);
            setRestrictedRegistrationSteps([]);
            navigateToUserConsent();
          }
        },
        onError: (error) => {
          toast({ description: error.message });
        },
      },
    );
  };

  const onConfirm = () => {
    const apiData = {
      kitId,
      address: {
        addressLine1: street,
        addressLine2: apartment,
        city,
        country,
        state,
        zip,
        isShippable: shippable,
      },
      dateOfBirth: formatDayJSDate(dob),
    };
    mutateConfirmation(apiData, {
      onSuccess: (data) => {
        if (data?.registrationAllowed) {
          callConsent();
        }
      },
    });
  };

  return (
    <RegistrationScreen
      title="Confirm Your Details"
      description="Take a look at your information below to ensure it’s correct. We may
          contact you using below information."
      content={
        <>
          <InfoSection
            className="mt-3"
            title="Registration Information"
            editPath={P.REGISTRATION_KIT_ID}
            items={[{ field: 'Kit ID', value: kitId }]}
          />

          <InfoSection
            className="mt-8"
            title="Account information"
            editPath={P.REGISTRATION_PERSONAL_INFORMATION}
            items={[
              { field: 'Email ID', value: email },
              { field: 'Name', value: `${firstName} ${lastName}` },
              { field: 'Birthday', value: dobReadableFormat },
              { field: 'Biological Sex', value: gender },
              { field: 'Address of Residency', value: formattedAddress },
              { field: 'Phone Number', value: `${phoneCode} ${phone}` },
            ]}
          />
        </>
      }
      primaryActionLabel="My information is correct"
      primaryActionOnClick={onConfirm}
      isLoading={loading}
      checkForRegisterStore={RegistrationTrack.RegistrationConfirmation}
    />
  );
};
